import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { BudgetCategoryService } from '../../../../services/budget-category.service';
import { ToastService } from '../../../../services/toast.service';
import { BudgetSubcategoryService } from '../../../../services/budget-subcategory.service';
import { LoadingService } from '../../../../services/loading.service';
import { AuthorizationService } from '../../../../services/authorization.service';

@Component({
  selector: 'app-new-subcategory',
  templateUrl: './new-subcategory.component.html',
  styleUrl: './new-subcategory.component.scss'
})
export class NewSubcategoryComponent implements OnInit, OnChanges  {

  @Input() subCategoryId: number | null = null;
  @Input() readonly: boolean = false;
  @Output() retrieveData = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();
  
  public categories: any;
  budgetSubcategoryForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private _budgetCategoryService : BudgetCategoryService,
    private _budgetSubcategoryService : BudgetSubcategoryService,
    private toastService: ToastService,
    private _loadingService : LoadingService,
    private authService: AuthorizationService
  ) {
    this.budgetSubcategoryForm = this.fb.group({
      budgetCategoryId: [null, Validators.required],
      name: ['', Validators.required],
      amount: [null, [Validators.required, Validators.min(0)]],
      active: [true]
    });
  }
  
  // Verificar si puede guardar (crear o actualizar)
  get canSave(): boolean {
    return this.authService.hasPermission('CONFIG_BUDGETS_SUBCATEGORY');
  }
  ngOnInit(): void {
    // Asegurar que el formulario esté limpio al inicializar
    if (!this.subCategoryId) {
      this.resetForm();
    }
  }

  // Método para obtener el título dinámico del modal
  getModalTitle(): string {
    if (this.readonly) {
      return 'Detalles de Subcategoría';
    }
    return this.subCategoryId ? 'Editar Subcategoría de Presupuesto' : 'Nueva Subcategoría de Presupuesto';
  }
  
  // Método para obtener la descripción del modal
  getModalDescription(): string {
    if (this.readonly) {
      return 'Información de la subcategoría de presupuesto';
    }
    return this.subCategoryId 
      ? 'Modifique los datos de la subcategoría' 
      : 'Complete la información para crear una nueva subcategoría de presupuesto';
  }

  loadDataOnOpen(): void {
    this.getCategories().catch(error => {
      console.error('Error loading categories on open:', error);
    });
  }

  getCategories(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._budgetCategoryService.getAll().subscribe({
        next: (data) => {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const categoriesData = Array.isArray(data.data) ? data.data : (data.data?.data || []);
          // Mapear las categorías al formato esperado por p-dropdown
          this.categories = categoriesData.map((category: any) => ({
            label: category.name,
            value: category.id
          }));
          console.log('Categories loaded:', this.categories); // Debug log
          resolve();
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          reject(error);
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['subCategoryId']) {
      if (this.subCategoryId) {
        // Asegurar que las categorías estén cargadas antes de cargar la subcategoría
        this.loadCategoriesAndThenSubcategory(this.subCategoryId);
      } else {
        // Si no hay subCategoryId, resetear el formulario para modo creación
        this.resetForm();
        // Cargar categorías para el modo creación
        this.getCategories().catch(error => {
          console.error('Error loading categories for creation mode:', error);
        });
      }
    }
  }

  // Método para cargar categorías y luego la subcategoría
  private loadCategoriesAndThenSubcategory(subCategoryId: number): void {
    this.getCategories().then(() => {
      // Una vez que las categorías están cargadas, cargar la subcategoría
      this.loadSubCategory(subCategoryId);
    }).catch(error => {
      console.error('Error loading categories:', error);
      // Intentar cargar la subcategoría de todas formas
      this.loadSubCategory(subCategoryId);
    });
  }

  
  loadSubCategory(id: number) {
    this._budgetSubcategoryService.getById(id).subscribe({
      next: (subcategory: any) => {
        console.log('Subcategory data received:', subcategory.data); // Debug log
        
        // Extraer budgetCategoryId desde budgetCategory o budgetCategoryId
        let budgetCategoryId = null;
        
        if (subcategory.data.budgetCategoryId) {
          // Caso 1: El backend envía budgetCategoryId directamente
          budgetCategoryId = subcategory.data.budgetCategoryId;
        } else if (subcategory.data.budgetCategory && subcategory.data.budgetCategory.id) {
          // Caso 2: El backend envía el objeto budgetCategory completo
          budgetCategoryId = subcategory.data.budgetCategory.id;
        } else {
          console.warn('No se pudo obtener budgetCategoryId de la respuesta del backend');
        }
        
        console.log('BudgetCategoryId extracted:', budgetCategoryId); // Debug log
        
        // Verificar si la subcategoría tiene una categoría asignada
        if (budgetCategoryId === null) {
          console.warn('Esta subcategoría no tiene una categoría principal asignada. Posible problema de datos.');
          this.toastService.showToast('warning', 'Esta subcategoría no tiene una categoría principal asignada. Seleccione una categoría.');
        }
        
        this.budgetSubcategoryForm.patchValue({
          budgetCategoryId: budgetCategoryId,
          name: subcategory.data.name,
          amount: subcategory.data.amount,
          active: subcategory.data.active
        });
        
        console.log('Form patched with values:', this.budgetSubcategoryForm.value); // Debug log
      },
      error: (error) => {
        console.error('Error loading subcategory:', error);
      }
    });
  }

  onSubmit() {
    // Validar permisos antes de guardar
    if (!this.authService.checkPermissionWithFeedback('CONFIG_BUDGETS_SUBCATEGORY', 'No tienes permisos para gestionar subcategorías')) {
      return;
    }
    
    this._loadingService.show();
    
    const newCategory = {
      ...this.budgetSubcategoryForm.value,
      budgetCategoryId: Number(this.budgetSubcategoryForm.value.budgetCategoryId)
    };
    if (this.budgetSubcategoryForm.valid) {
      if (this.subCategoryId) {
        // Actualizar categoría existente
        this._budgetSubcategoryService.update(this.subCategoryId, newCategory).subscribe({
          next: ()=> {
            this.handleSuccess('Subcategoría actualizada correctamente.');
          },
          error : () => {
            this.handleError('Se produjo un error al actualizar la subcategoría.');
          }
        });
      } else {
        // Crear nueva categoría
        this._budgetSubcategoryService.insert(newCategory).subscribe({
          next: ()=> {
            this.handleSuccess('Subcategoría creada correctamente.');
          },
          error : () => {
            this.handleError('Se produjo un error al crear la subcategoría.');
          }
        });
      }
    }
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.resetForm();
    this.closeModal.emit();
    this.retrieveData.emit();
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
    this.closeModal.emit();
  }

  private resetForm(): void {
    this.budgetSubcategoryForm.reset({
      budgetCategoryId: '',
      name: '',
      amount: '',
      active: true
    });
    this.subCategoryId = null;
    this.categories = null;
  }

}
